import React, { useState, useEffect } from 'react';
import { Table, Form, Button, message, Space, Typography, Modal, Input, Tabs } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import API_ENDPOINTS from '../config/api';

// Add the font configuration here, after imports and before component
const Font = ReactQuill.Quill.import('formats/font');
Font.whitelist = ['arial', 'times-new-roman', 'georgia', 'helvetica'];
ReactQuill.Quill.register(Font, true);

const { Title } = Typography;
const { TextArea } = Input;

const EditAboutUs = () => {
    const [content, setContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [editorHtml, setEditorHtml] = useState('');
    const [form] = Form.useForm();

    // Updated Quill modules configuration with more formatting options
    const modules = {
        toolbar: [
            [{ 'font': [] }],  // Add font family options
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'size': ['small', false, 'large', 'huge'] }],  // Add font size options
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],  // Custom color pickers
            [{ 'align': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            ['link'],
            ['clean']
        ]
    };

    const formats = [
        'font',
        'header',
        'size',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'align',
        'list', 'bullet',
        'indent',
        'link'
    ];

    // Custom CSS to add to your stylesheet
    const editorStyle = {
        '.ql-editor': {
            minHeight: '300px',
            fontSize: '16px',
            lineHeight: '1.6'
        },
        '.ql-snow .ql-picker.ql-font': {
            width: '150px'  // Make font picker wider
        }
    };

    useEffect(() => {
        fetchAboutUsContent();
    }, []);

    const fetchAboutUsContent = async () => {
        try {
            const response = await fetch(`${API_ENDPOINTS.ABOUTUS}/aboutus`);
            const result = await response.json();
            
            if (result.status === 'success') {
                setContent(result.data);
            } else {
                message.error(result.message);
            }
        } catch (err) {
            message.error('Failed to fetch content');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingRecord(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        setEditorHtml(record.contents);
        form.setFieldsValue({
            page_name: record.page_name,
            contents: record.contents,
            status: record.status
        });
        setIsModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            const response = await fetch(`${API_ENDPOINTS.ABOUTUS}/aboutus?id=${id}`, {
                method: 'DELETE'
            });
            const result = await response.json();

            if (result.status === 'success') {
                message.success('Content deleted successfully');
                fetchAboutUsContent();
            } else {
                message.error(result.message);
            }
        } catch (err) {
            message.error('Failed to delete content');
        }
    };

    const handleSubmit = async (values) => {
        try {
            const method = editingRecord ? 'PUT' : 'POST';
            const data = editingRecord ? { ...values, id: editingRecord.id } : values;

            const response = await fetch(`${API_ENDPOINTS.ABOUTUS}/aboutus`, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data)
            });

            const result = await response.json();
            
            if (result.status === 'success') {
                message.success(editingRecord ? 
                    'Content updated successfully' : 
                    'Content created successfully'
                );
                setIsModalVisible(false);
                fetchAboutUsContent();
            } else {
                message.error(result.message);
            }
        } catch (err) {
            message.error('Failed to save content');
        }
    };

    const handleEditorChange = (content) => {
        setEditorHtml(content);
        form.setFieldsValue({ contents: content });
    };

    const columns = [
        {
            title: 'Page Name',
            dataIndex: 'page_name',
            key: 'page_name',
        },
        {
            title: 'Contents',
            dataIndex: 'contents',
            key: 'contents',
            width: '60%',
            render: (html) => (
                <div 
                    dangerouslySetInnerHTML={{ __html: html }}
                    style={{ 
                        padding: '20px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '4px',
                        background: '#fff'
                    }} 
                />
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                >
                    Edit
                </Button>
            )
        }
    ];

    return (
        <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '16px' }}>
                <Title level={2}>About Us Content</Title>
            </div>

            <Table
                loading={loading}
                dataSource={content}
                columns={columns}
                rowKey="id"
            />

            <Modal
                title={editingRecord ? 'Edit Content' : 'Add New Content'}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                width={1200}
                style={{ top: 20 }}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="page_name"
                        label="Page Name"
                        rules={[{ required: true, message: 'Please input page name!' }]}
                    >
                        <Input disabled={!!editingRecord} />
                    </Form.Item>

                    <Form.Item
                        name="contents"
                        label="Contents"
                        rules={[{ required: true, message: 'Please input contents!' }]}
                    >
                        <Tabs
                            items={[
                                {
                                    key: 'editor',
                                    label: 'Rich Text Editor',
                                    children: (
                                        <ReactQuill 
                                            theme="snow"
                                            modules={modules}
                                            formats={formats}
                                            value={editorHtml}
                                            onChange={handleEditorChange}
                                            style={{ 
                                                height: '400px',  // Increased height
                                                marginBottom: '50px'
                                            }}
                                        />
                                    )
                                },
                                {
                                    key: 'html',
                                    label: 'HTML Code',
                                    children: (
                                        <TextArea
                                            value={editorHtml}
                                            onChange={(e) => handleEditorChange(e.target.value)}
                                            rows={15}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontSize: '14px'
                                            }}
                                        />
                                    )
                                }
                            ]}
                        />
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label="Status"
                        initialValue="active"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item style={{ marginTop: '50px' }}>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                {editingRecord ? 'Update' : 'Create'}
                            </Button>
                            <Button onClick={() => setIsModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default EditAboutUs; 